/**
 * admin/shared.js — Shared utilities for Admin Console tabs.
 *
 * Exports: adminApi (axios instance with JWT), API base URL, and reusable components.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowUpDown, RefreshCw } from "lucide-react";
import { setAdminToken } from "@/hooks/useAdminAuth";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HTTP_UNAUTHORIZED = 401;

/* ---- Authenticated axios instance for admin calls ----
 *
 * JWT is persisted in sessionStorage via the centralised `useAdminAuth`
 * hook (see /app/frontend/src/hooks/useAdminAuth.js). Rationale:
 *   - sessionStorage clears on tab close — matches the intended session
 *     lifetime for an admin console.
 *   - httpOnly cookies would need cross-origin cookie setup with the
 *     preview backend and a CSRF flow.
 *   - XSS risk is bounded: no user-generated HTML is rendered unescaped,
 *     React escapes by default, the JWT expires in 1 hour, and only the
 *     admin password can issue one.
 *   - All read/write goes through `useAdminAuth` / `setAdminToken` so a
 *     future migration to httpOnly cookies touches a single helper.
 */
export const adminApi = axios.create({ withCredentials: true });

adminApi.interceptors.request.use((config) => {
  // Break-glass password sessions carry a Bearer token in sessionStorage.
  // Google-authenticated admins carry the httpOnly session cookie instead,
  // so the header is simply absent for them.
  const token = sessionStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === HTTP_UNAUTHORIZED) {
      setAdminToken(null);
      window.dispatchEvent(new Event("admin-logout"));
    }
    return Promise.reject(err);
  },
);


/* ---- Shared helpers ---- */

// Re-export the zero-dep error helper so existing imports like
// `import { formatApiError } from "@/components/admin/shared"` keep
// working. New code can import directly from `@/utils/errors`.
export { formatApiError } from "@/utils/errors";


/* ---- Shared utility components ---- */

export function SourceDot({ source }) {
  if (!source) return null;
  const isAuto = source === 'justetf';
  return (
    <span
      data-testid={`source-${source}`}
      title={isAuto ? 'Auto-retrieved from justETF' : 'Manually entered'}
      className={`flex-shrink-0 w-2 h-2 rounded-full ${isAuto ? 'bg-amber-400' : 'bg-violet-400'}`}
    />
  );
}

export function SortTh({ field, current, dir, onClick, children }) {
  const active = current === field;
  return (
    <th className="admin-th cursor-pointer select-none group" onClick={() => onClick(field)}>
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 transition-opacity ${active ? 'opacity-100 text-blue-400' : 'opacity-0 group-hover:opacity-50'}`} />
        {active && dir === 1 && <span className="text-[10px] text-blue-400">A</span>}
        {active && dir === -1 && <span className="text-[10px] text-blue-400">Z</span>}
      </span>
    </th>
  );
}

export function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16 text-zinc-500">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading...
    </div>
  );
}

/* Re-export hooks for convenience */
export { useState, useEffect, useCallback };
